import { getReadableDateRange } from 'app-components/utils/date';
import { Timestep } from 'app-components/utils/date/predefinedRange';
import type RootState from 'app-components/utils/redux-store/rootState';
import { List as ImList, Record, RecordOf } from 'immutable';
import _groupBy from 'lodash/groupBy';
import { createSelector } from 'reselect';
import { accountsUtils } from '../accounts';
import { AccountingType } from '../report-configurations/reportConfigurationsTypes';
import { transactionsSelectors } from '../transactions';
import { SplitItemProps, mkTransactionItem } from '../transactions/transactionsTypes';
import { createPlaceholderTxn } from '../transactions/transactionsUtils';
import reportsAdapter from './generatedReportsEntityAdapter';
import { GENERATED_REPORTS_REDUCER_KEY, ReportRow } from './generatedReportsTypes';

export const getStore = (state: Pick<RootState, GENERATED_REPORTS_REDUCER_KEY>) => state[GENERATED_REPORTS_REDUCER_KEY];
const reportsSelectors = reportsAdapter.getSelectors(getStore);

export const getEntity = (state: Pick<RootState, GENERATED_REPORTS_REDUCER_KEY>, { location }: { location: string }) =>
  reportsSelectors.selectById(state, location);

export const getIsGenerating = createSelector(getEntity, (entity) => entity?.isGenerating);
export const getReport = createSelector(getEntity, (entity) => entity?.report);
export const getError = createSelector(getEntity, (entity) => entity?.error);

export const getGraphDataFromReport = createSelector(
  getReport,
  (_state: unknown, props: { timestep?: Timestep }) => props.timestep,
  (report, timestep = 'MONTH') => {
    const rowsByParentId = _groupBy(report?.rows, ({ parentId }) => parentId ?? '-1');
    const graphRows = rowsByParentId['-1'] ?? [];
    const cols = report?.columns ?? [];
    if (graphRows) {
      const income = graphRows?.find((row) => row?.entityId === 'INCOME');
      const spending = graphRows?.find((row) => row?.entityId === 'EXPENSE');
      const netIncome = graphRows?.find((row) => row?.entityId === 'NET_INCOME');

      // the last column that qcs returns is the total column
      // if the date range is less than the interval we just display the total column as the data
      const graphData =
        graphRows[0]?.cellData?.length > 1 ? graphRows[0]?.cellData?.slice(0, -1) : graphRows[0]?.cellData;
      return graphData?.map((_data, index) => {
        const column = cols[index];
        const totals = {
          incomeTotal: income ? Number(income?.cellData?.[index].value) : 0,
          spendingTotal: spending ? -1 * Number(spending?.cellData?.[index].value) : 0,
          netIncomeTotal: netIncome ? Number(netIncome?.cellData?.[index].value) : 0,
          timestep,
        };
        if (column?.headerType === 'QCS_ENTITY') {
          return {
            category:
              column.headerValue.qcs_entity === 'ACCOUNT'
                ? accountsUtils.getAccountString(column.headerValue.id) || 'Unknown'
                : column.headerValue.id,
            ...totals,
          };
        }
        if (column?.headerType === 'DATE_RANGE') {
          return {
            category: getReadableDateRange(timestep, column.headerValue.startOn, column.headerValue.endOn),
            startOn: column?.headerValue?.startOn,
            endOn: column?.headerValue?.endOn,
            ...totals,
          };
        }
        if (column?.headerType === 'STRING') {
          return {
            category: column.headerValue.id,
            ...totals,
          };
        }
        throw new Error(`Expected column type of DATE_RANGE, QCS_ENTITY, or STRING`);
      });
    }
    return null;
  },
);

export const getReportTransactionItems = createSelector(
  [
    getReport,
    transactionsSelectors.getResourcesById,
    (_state: unknown, props: { rowId?: string }) => props.rowId,
    (_state: unknown, props: { entityId?: string }) => props.entityId,
    (_state: unknown, props: { columnId?: string }) => Number(props.columnId ?? -1),
    (_state: unknown, props: { accountingType: AccountingType }) => props.accountingType,
    (_state: unknown, props: { businessId?: string }) => props.businessId,
  ],
  (report, transactionsById, rowID, entityId = 'NET_INCOME', columnId, accountingType, businessId) => {
    // tax report could contain multiple businesses, so we need to only the the relevate items for the specified business
    const getRowForBusinessId = (businessId: string): ReportRow | undefined => {
      // recusively find the parent node and return true if it matches the business id, otherwise return false
      const isCorrectBusiness = (candidateRow: ReportRow): boolean => {
        if (candidateRow.parentId === '-1') {
          return candidateRow.entityId === businessId;
        } else {
          const parentRow = report?.rows.find((row) => row.id === candidateRow.parentId);
          return parentRow ? isCorrectBusiness(parentRow) : false;
        }
      };
      // find all of the matching tax line item rows
      const candiates = report?.rows.filter((row) => (entityId ? row.entityId === entityId : row.id === rowID));
      // return the row matching corresonding to the specific business
      if (candiates && candiates.length > 0) {
        for (let i = 0; i < candiates.length; i++) {
          if (isCorrectBusiness(candiates[i])) {
            return candiates[i];
          }
        }
      }
      return;
    };

    // check entityId, fallback on rowid, when categories or other fields are changed, the report can change and row is no longer useful but a good backup
    const getRow = () => report?.rows.find((row) => (entityId ? row.entityId === entityId : row.id === rowID));
    const row = businessId ? getRowForBusinessId(businessId) : getRow();

    return ImList(
      row?.cellData.at(columnId)?.items?.map((cellItem) => {
        const txn = transactionsById.get(cellItem.txnId);
        if (txn) {
          if (!Record.isRecord(txn)) {
            assert(false, 'transaction is not a record in transactionsById');
            return createPlaceholderTxn(
              txn.payee || 'N/A',
              cellItem.amount,
              cellItem.amountOn,
              cellItem.txnId,
              cellItem.splitItemId,
            );
          }
          const split = cellItem?.splitItemId
            ? txn.split?.items?.find((splitItem) => splitItem?.id === cellItem?.splitItemId)
            : undefined;
          const isPartial = (split?.amount || txn?.amount) !== cellItem.amount;
          return mkTransactionItem({
            transaction: txn,
            splitItem: split as RecordOf<SplitItemProps>,
            item: {
              ...txn.toObject(),
              ...(Record.isRecord(split) ? split?.toObject() : split),
              ...(accountingType === 'CASH' && {
                isPartial,
                unpaidAmount: isPartial ? (split?.amount || txn?.amount) - cellItem.amount : undefined,
                paidAmount: cellItem.amount,
                paymentTxnId: cellItem?.paymentTxnId,
                postedOn: cellItem?.amountOn,
              }),
            },
          });
        }
        return createPlaceholderTxn('NA', cellItem.amount, cellItem.amountOn, cellItem.txnId, cellItem.splitItemId);
      }),
    );
  },
);

export const rowsByParentId = createSelector(getReport, (report) =>
  _groupBy(report?.rows, (row) => row.parentId ?? '-1'),
);
